import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import React from "react";
import { useTranslation } from "react-i18next";

import BackButton from "@/components/back-button";
import Button from "@/components/button";
import { AvailabilityPicker } from "@/components/availability-picker";
import type { AvailabilityWindow } from "@/components/availability-picker";
import { Combobox } from "@/components/combobox";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useGetProfileQuery } from "@/queries/hooks/profileHooks";
import { postWorkerAvailability } from "@/queries/api/searchApi";
import { QueryKeys } from "@/queries/queryKeys";
import { useOrderedRoleOptions } from "@/utils/hooks/use-ordered-role-options";
import { useRole } from "@/utils/hooks/use-role";
import { notify } from "@/utils";

const ROLES = ["worker", "employer"] as const;
type Role = (typeof ROLES)[number];

export const Route = createFileRoute("/app/$role/_app/post-availability/")({
  component: PostAvailabilityPage,
  beforeLoad: ({ params }) => {
    if (!ROLES.includes(params.role as Role)) {
      throw new Error(`Invalid role: ${params.role}`);
    }
    if (params.role !== "worker") {
      throw new Error("Only workers can post availability");
    }
  },
});

function PostAvailabilityPage() {
  const { t } = useTranslation("dashboard");
  const profileQuery = useGetProfileQuery();

  return (
    <div className="flex flex-col gap-5 w-full px-4 lg:pl-8 py-9 max-w-[1320px] mx-auto">
      <BackButton />
      <Card className="bg-white border-none max-w-[1440px]">
        <CardHeader>
          <CardTitle className="text-[20px] font-bold">
            {t("postAvailabilityPage.title")}
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-6 max-w-[760px]">
          {profileQuery.isPending ? (
            <p className="body-small text-garnet">
              {t("postAvailabilityPage.loading")}
            </p>
          ) : (
            <PostAvailabilityForm
              initialRoles={profileQuery.data?.profile.preferred_roles ?? []}
              initialWindows={
                profileQuery.data?.profile.posted_availability ?? []
              }
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function PostAvailabilityForm({
  initialRoles,
  initialWindows,
}: {
  initialRoles: Array<string>;
  initialWindows: Array<AvailabilityWindow>;
}) {
  const { t } = useTranslation("dashboard");
  const navigate = useNavigate();
  const role = useRole();
  const queryClient = useQueryClient();
  const rolesOptions = useOrderedRoleOptions();

  const [selectedRoles, setSelectedRoles] =
    React.useState<Array<string>>(initialRoles);
  const [windows, setWindows] =
    React.useState<Array<AvailabilityWindow>>(initialWindows);

  const handleWindowsChange = React.useCallback(
    (w: Array<AvailabilityWindow>) => setWindows(w),
    [],
  );

  const mutation = useMutation({
    mutationFn: postWorkerAvailability,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.PROFILE] });
      notify.success(t("postAvailabilityPage.saved"));
      navigate({ to: "/app/$role/dashboard", params: { role } });
    },
    onError: (err: any) => {
      notify.error(err?.message || t("postAvailabilityPage.saveError"));
    },
  });

  const minDate = React.useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const canSubmit =
    selectedRoles.length > 0 && windows.length > 0 && !mutation.isPending;

  function handleSubmit() {
    if (selectedRoles.length === 0) {
      notify.error(t("postAvailabilityPage.selectRole"));
      return;
    }
    if (windows.length === 0) {
      notify.error(t("postAvailabilityPage.selectAvailability"));
      return;
    }
    mutation.mutate({ role_names: selectedRoles, windows });
  }

  return (
    <>
      <section className="flex flex-col gap-2">
        <label className="body-small text-garnet font-semibold">
          {t("postAvailabilityPage.rolesLabel")}
        </label>
        <Combobox
          multi
          options={rolesOptions}
          value={selectedRoles}
          onChange={(v) => setSelectedRoles(v)}
          placeholder={t("searchBar.searchShiftsByRole")}
          searchPlaceholder={t("searchBar.typeARole")}
        />
      </section>

      <section className="flex flex-col gap-2">
        <label className="body-small text-garnet font-semibold">
          {t("postAvailabilityPage.availabilityLabel")}
        </label>
        <AvailabilityPicker
          minDate={minDate}
          initialWindows={initialWindows}
          showContinue={false}
          onChange={handleWindowsChange}
        />
      </section>

      <div className="flex justify-end pt-2">
        <Button
          variant="primary"
          size="lg"
          onClick={handleSubmit}
          disabled={!canSubmit}
        >
          {mutation.isPending
            ? t("postAvailabilityPage.submitting")
            : t("postAvailabilityPage.submit")}
        </Button>
      </div>
    </>
  );
}
